import React, { useState, useEffect, useRef } from 'react';
import { Download, Filter, TrendingUp, TrendingDown, ChevronUp, ChevronDown, Columns } from 'lucide-react';
import { Button, Input } from '../ui';
import Select from '../ui/Select';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';
import BookingModal from '../booking/BookingModal';
import InvoiceRevampModal from '../invoice/InvoiceRevampModal';

const ProfitLossReportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [selectedItineraryId, setSelectedItineraryId] = useState<number | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [sortedRecords, setSortedRecords] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'} | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [filters, setFilters] = useState({
    agencyId: '',
    itineraryId: '',
    travelDateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    travelDateTo: new Date().toISOString().split('T')[0],
    operationStatus: ''
  });
  const [agencies, setAgencies] = useState<any[]>([]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef<HTMLDivElement>(null);

  const allColumns = [
    { key: 'itinerary_id', label: 'Itinerary', default: true },
    { key: 'travel_date', label: 'Travel Date', default: true },
    { key: 'invoice_usd', label: 'Invoice (USD)', default: true },
    { key: 'invoice_idr', label: 'Invoice (IDR)', default: true },
    { key: 'invoice_inr', label: 'Invoice (INR)', default: true },
    { key: 'invoice_eq_idr', label: 'Invoice Eq. IDR', default: false },
    { key: 'paid_usd', label: 'Paid (USD)', default: true },
    { key: 'paid_idr', label: 'Paid (IDR)', default: true },
    { key: 'paid_inr', label: 'Paid (INR)', default: true },
    { key: 'paid_eq_idr', label: 'Paid Eq. IDR', default: false },
    { key: 'total_expenses', label: 'Expenses (IDR)', default: true },
    { key: 'profit_loss', label: 'Profit/Loss', default: true },
    { key: 'margin_percent', label: 'Margin %', default: true },
    { key: 'operation_cost_status', label: 'Op Status', default: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    () => new Set(allColumns.filter(c => c.default).map(c => c.key))
  );

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isVisible = (key: string) => visibleColumns.has(key);

  useEffect(() => {
    loadAgencies();
    loadRecords();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target as Node)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAgencies = async () => {
    try {
      const response = await api.get('/reports/vendors');
      if (response.data.success) {
        setAgencies(response.data.data);
      }
    } catch (error) {
      console.error('Error loading agencies:', error);
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
      if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);
      
      if (filters.agencyId) params.append('agency_id', filters.agencyId);
      if (filters.itineraryId) params.append('itinerary_id', filters.itineraryId);

      const apiUrl = `/reports/profit-loss?${params.toString()}`;
      console.log('🔍 Profit-Loss API Call:', apiUrl);
      console.log('📊 Filters:', filters);
      
      const response = await api.get(apiUrl);
      if (response.data.success) {
        let data = response.data.data;
        if (filters.operationStatus) {
          data = data.filter((r: any) => (r.operation_cost_status || 'PENDING') === filters.operationStatus);
        }
        setRecords(data);
        setSortedRecords(data);
        setAnalytics(response.data.analytics);
        console.log('📈 Analytics received:', response.data.analytics);
      }
    } catch (error) {
      console.error('Error loading profit loss records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    if (currency === 'INR') {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0
      }).format(amount);
    }
    if (currency === 'IDR') {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
      }).format(amount);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatIDR = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const calculateAnalytics = () => {
    if (analytics) {
      return {
        totalRevenue: analytics.total_received,
        totalExpenses: analytics.total_expenses,
        totalProfit: analytics.actual_profit,
        avgMargin: analytics.collection_rate,
        totalInvoiceAmount: analytics.total_invoice_amount,
        idealProfit: analytics.total_ideal_profit,
        idealProfitRate: analytics.ideal_profit_rate,
        companyOverhead: analytics.company_overhead_idr || 0,
        grossProfit: analytics.gross_profit || 0,
        netProfit: analytics.net_profit || 0,
      };
    }
    
    const totalRevenue = sortedRecords.reduce((sum, record) => sum + record.received_idr, 0);
    const totalExpenses = sortedRecords.reduce((sum, record) => sum + record.total_expenses, 0);
    const totalProfit = totalRevenue - totalExpenses;
    const avgMargin = sortedRecords.length > 0 ? sortedRecords.reduce((sum, record) => sum + record.margin_percent, 0) / sortedRecords.length : 0;
    
    return { totalRevenue, totalExpenses, totalProfit, avgMargin, totalInvoiceAmount: 0, idealProfit: 0, idealProfitRate: 0, companyOverhead: 0, grossProfit: 0, netProfit: 0 };
  };

  const calculatedAnalytics = calculateAnalytics();

  const openInvoiceModal = async (record: any) => {
    try {
      const itinId = record.itinerary_id; // e.g. WH_11057
      const response = await api.get(`/invoice-revamp/?itinerary_id=${itinId.replace('WH_', '')}`);
      if (response.data.success && response.data.data?.length > 0) {
        setSelectedInvoice(response.data.data[0]);
        setShowInvoiceModal(true);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
    }
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...records].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setSortedRecords(sorted);
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ChevronUp className="w-4 h-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">📊 Profit & Loss Report</h1>
        <p className="text-gray-600">Analyze profitability across converted itineraries</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Invoice Amount</p>
              <p className="text-2xl font-bold text-blue-600">{formatIDR(calculatedAnalytics.totalInvoiceAmount || 0)}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue (Received)</p>
              <p className="text-2xl font-bold text-green-600">{formatIDR(calculatedAnalytics.totalRevenue)}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Vendor Expenses</p>
              <p className="text-2xl font-bold text-red-600">{formatIDR(calculatedAnalytics.totalExpenses)}</p>
            </div>
            <TrendingDown className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Company Expenses</p>
              <p className="text-2xl font-bold text-orange-600">{formatIDR(calculatedAnalytics.companyOverhead)}</p>
            </div>
            <TrendingDown className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Gross Profit</p>
              <p className={`text-2xl font-bold ${calculatedAnalytics.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatIDR(calculatedAnalytics.grossProfit)}
              </p>
              <p className="text-xs text-gray-400">Revenue - Vendor Expenses</p>
            </div>
            {calculatedAnalytics.grossProfit >= 0 ? 
              <TrendingUp className="w-8 h-8 text-green-500" /> : 
              <TrendingDown className="w-8 h-8 text-red-500" />
            }
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Profit</p>
              <p className={`text-2xl font-bold ${calculatedAnalytics.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatIDR(calculatedAnalytics.netProfit)}
              </p>
              <p className="text-xs text-gray-400">Gross Profit - Company Expenses</p>
            </div>
            {calculatedAnalytics.netProfit >= 0 ? 
              <TrendingUp className="w-8 h-8 text-green-500" /> : 
              <TrendingDown className="w-8 h-8 text-red-500" />
            }
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Profit %</p>
              <p className={`text-2xl font-bold ${calculatedAnalytics.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {calculatedAnalytics.totalRevenue > 0 ? ((calculatedAnalytics.netProfit / calculatedAnalytics.totalRevenue) * 100).toFixed(1) : '0.0'}%
              </p>
              <p className="text-xs text-gray-400">Net Profit / Revenue</p>
            </div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${calculatedAnalytics.netProfit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <span className={`text-sm font-bold ${calculatedAnalytics.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>%</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ideal Profit</p>
              <p className={`text-2xl font-bold ${(calculatedAnalytics.idealProfit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatIDR(calculatedAnalytics.idealProfit || 0)}
              </p>
            </div>
            {(calculatedAnalytics.idealProfit || 0) >= 0 ? 
              <TrendingUp className="w-8 h-8 text-green-500" /> : 
              <TrendingDown className="w-8 h-8 text-red-500" />
            }
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ideal Profit %</p>
              <p className={`text-2xl font-bold ${(calculatedAnalytics.idealProfitRate || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(calculatedAnalytics.idealProfitRate || 0).toFixed(1)}%
              </p>
            </div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${(calculatedAnalytics.idealProfitRate || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <span className={`text-sm font-bold ${(calculatedAnalytics.idealProfitRate || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <Button variant="secondary" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Excel
        </Button>
        <Button variant="secondary" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          PDF
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
            <Input
              type="date"
              value={filters.travelDateFrom}
              onChange={(e) => handleFilterChange('travelDateFrom', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
            <Input
              type="date"
              value={filters.travelDateTo}
              onChange={(e) => handleFilterChange('travelDateTo', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label>
            <SearchableSelect
              options={agencies.map(agency => {
                const agencyName = typeof agency === 'string' ? agency : agency.name || agency.agency_name || agency;
                return { value: agencyName === 'All Agencies' ? '' : agencyName, label: agencyName };
              })}
              value={filters.agencyId}
              onChange={(value) => handleFilterChange('agencyId', value as string)}
              placeholder="Select Agency"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary ID</label>
            <Input
              type="text"
              value={filters.itineraryId}
              onChange={(e) => handleFilterChange('itineraryId', e.target.value)}
              placeholder="WH_1234"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Operation Status</label>
            <Select
              value={filters.operationStatus}
              onChange={(e) => handleFilterChange('operationStatus', e.target.value)}
              className="w-full"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="INPROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
            </Select>
          </div>
        </div>
        
        <Button onClick={loadRecords} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Profit & Loss Records ({sortedRecords.length})</h2>
          <div className="relative" ref={columnPickerRef}>
            <Button
              variant="secondary"
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              className="flex items-center gap-2 text-sm"
            >
              <Columns className="w-4 h-4" />
              Columns
            </Button>
            {showColumnPicker && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-52 py-2">
                {allColumns.map(col => (
                  <label key={col.key} className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                      className="rounded"
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {allColumns.filter(c => isVisible(c.key)).map(col => (
                  <th key={col.key} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort(col.key)}>
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      {col.label}
                      {getSortIcon(col.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRecords.map((record, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {isVisible('itinerary_id') && (
                    <td className="px-3 py-4 text-sm font-medium text-gray-900">
                      {record.quotation_number ? (
                        <div
                          className="cursor-pointer hover:underline"
                          onClick={() => setSelectedItineraryId(parseInt(record.itinerary_id.replace('WH_', '')))}
                        >
                          <div className="font-mono font-bold text-blue-700">{record.quotation_number}/{record.version_number}</div>
                          <div className="text-[10px] text-gray-400">{record.itinerary_id}</div>
                        </div>
                      ) : (
                        <div
                          className="truncate max-w-[100px] cursor-pointer hover:underline text-blue-700"
                          title={record.itinerary_id}
                          onClick={() => setSelectedItineraryId(parseInt(record.itinerary_id.replace('WH_', '')))}
                        >
                          {record.itinerary_id}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 truncate max-w-[120px]" title={record.guest_name}>{record.guest_name}</div>
                      <div className="text-[10px] text-gray-400 truncate max-w-[100px]" title={record.agency_name}>{record.agency_name}</div>
                    </td>
                  )}
                  {isVisible('travel_date') && (
                    <td className="px-3 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {record.travel_date ? new Date(record.travel_date).toLocaleDateString('en-GB') : 'N/A'}
                    </td>
                  )}
                  {isVisible('invoice_usd') && (
                    <td className="px-3 py-4 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {record.invoice_usd ? formatCurrency(record.invoice_usd, 'USD') : <span className="text-gray-300">—</span>}
                    </td>
                  )}
                  {isVisible('invoice_idr') && (
                    <td className="px-3 py-4 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {record.invoice_idr ? formatIDR(record.invoice_idr) : <span className="text-gray-300">—</span>}
                    </td>
                  )}
                  {isVisible('invoice_inr') && (
                    <td className="px-3 py-4 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {record.invoice_inr ? formatCurrency(record.invoice_inr, 'INR') : <span className="text-gray-300">—</span>}
                    </td>
                  )}
                  {isVisible('invoice_eq_idr') && (
                    <td className="px-3 py-4 text-sm text-gray-500 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {formatIDR(record.invoice_eq_idr || 0)}
                    </td>
                  )}
                  {isVisible('paid_usd') && (
                    <td className="px-3 py-4 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {record.paid_usd ? formatCurrency(record.paid_usd, 'USD') : <span className="text-gray-300">—</span>}
                    </td>
                  )}
                  {isVisible('paid_idr') && (
                    <td className="px-3 py-4 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {record.paid_idr ? formatIDR(record.paid_idr) : <span className="text-gray-300">—</span>}
                    </td>
                  )}
                  {isVisible('paid_inr') && (
                    <td className="px-3 py-4 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {record.paid_inr ? formatCurrency(record.paid_inr, 'INR') : <span className="text-gray-300">—</span>}
                    </td>
                  )}
                  {isVisible('paid_eq_idr') && (
                    <td className="px-3 py-4 text-sm text-gray-500 text-right cursor-pointer hover:bg-blue-50" onClick={() => openInvoiceModal(record)}>
                      {formatIDR(record.paid_eq_idr || 0)}
                    </td>
                  )}
                  {isVisible('total_expenses') && (
                    <td className="px-3 py-4 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50" onClick={() => setSelectedItineraryId(parseInt(record.itinerary_id.replace('WH_', '')))}>
                      {formatIDR(record.total_expenses)}
                    </td>
                  )}
                  {isVisible('profit_loss') && (
                    <td className="px-3 py-4 text-sm">
                      <div className={`flex items-center gap-1 justify-end ${record.profit_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {record.profit_loss >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        <span>{formatIDR(record.profit_loss)}</span>
                      </div>
                    </td>
                  )}
                  {isVisible('margin_percent') && (
                    <td className="px-3 py-4 text-sm text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${record.margin_percent >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {record.margin_percent}%
                      </span>
                    </td>
                  )}
                  {isVisible('operation_cost_status') && (
                    <td className="px-3 py-4 text-sm text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        record.operation_cost_status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        record.operation_cost_status === 'INPROGRESS' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {record.operation_cost_status || 'PENDING'}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          
          {sortedRecords.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">No profit & loss records found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>

      {selectedItineraryId && (
        <BookingModal
          itineraryId={selectedItineraryId}
          onClose={() => setSelectedItineraryId(null)}
        />
      )}

      {showInvoiceModal && selectedInvoice && (
        <InvoiceRevampModal
          invoice={selectedInvoice}
          onClose={() => { setShowInvoiceModal(false); setSelectedInvoice(null); }}
        />
      )}
    </div>
  );
};

export default ProfitLossReportPage;